// React
import React from 'react';
// External
import { Form, Col, Image, Row } from 'react-bootstrap';
import SelectSearch, { fuzzySearch } from 'react-select-search';
import isEmpty from 'lodash/isEmpty';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/dist/css/splide.min.css';
// Internal
import getFormFieldVal from 'utils/getFormFieldVal';
import { getPetTypeList, getPetBreedList } from 'services/petDetails';
import Selector from 'components/shared/atoms/Selector/Selector';
import SEX_OPTIONS from 'constants/sexOptions';
import imageFallback from 'assets/images/image-not-available.png';
import Fuse from "fuse.js";
// Styles
import './styles.scss';
// Models
import Pet from 'models/Pet';
import NumberFormat from 'react-number-format';
import { MdOutlineRefresh } from 'react-icons/md';
import CustomSelect from 'components/shared/atoms/CustomSelect/CustomSelect';

const WEIGHT_UNIT = {
    kg: 'Weight (Kg)',
    hand: 'Hands'
};

const capitalizeWords = value => value.replace(/\S+/g, word => word.charAt(0).toUpperCase() + word.slice(1));

/**
 * @param {string} props.data - The text to be display in the previous button
 * @param {string} props.className - The className to be added to the form
 */
class PetDetailsForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            formData: Pet(this.props.formData),
            petTypeOptions: [],
            petBreedOptions: [],
            petBreedQuery: this.props.formData && this.props.formData.breed ? this.props.formData.breed : '',
            errors: [],
            weightUnit: WEIGHT_UNIT.kg,
            customUnitRatio: 1, // default to 1,
            autoloadBreedDropdown: false,
            isLoading: false,
        };

        this.currentRotationMode = []
        this.rotatedDegrees = [0,90,180,270];

        this.petTagPhotoCarouselRef = React.createRef();

        this.getPetTypeOptions();

        this.dropdownRefs = {
            type: React.createRef(),
            breed: React.createRef(),
            sex: React.createRef(),
        };
        this.bOpenOptions = false;
        this.bEnterPressed = false;
    }

    componentDidMount() {
        // If there is a previous form data and breed data, get the corresponding breed options for it
        if (this.props.formData && this.props.formData.breed) {
            this.getPetBreedOptions(this.props.formData.breed, [], { page: 1 });
            this.setState({ autoloadBreedDropdown: true });
        }
    }

    handleInputChange(event) {
        const fieldObj = getFormFieldVal(event);
        if (['firstName', 'lastName'].includes(event.target.name)) {
            fieldObj[event.target.name] = capitalizeWords(fieldObj[event.target.name]);
        }

        // Updates the state
        this.setState((prevState) => {
            const formData = Pet({ ...prevState.formData, ...fieldObj });
            const errors = this.removeError(prevState.errors, event.target.name);
            return { formData, errors };
        });
    }

    /**
     * Handles the pet breed field blur event
     *
     * @param {String} strBreedQuery
     *
     * @returns {void}
     */
    handlePetBreedBlur(strBreedQuery) {
        this.setState(prevState => {
            let petBreedOptions = prevState.petBreedOptions;
            let petBreedQuery = prevState.petBreedQuery;
            let petBreed = prevState.formData.breed;
            let errors = prevState.errors;

            const breedIndex = this.getSelectedBreedIndex(petBreed);

            // If there is no selected breed, add no selected breed error
            if (breedIndex === -1 && !isEmpty(strBreedQuery)) {
                this.addError(errors, 'breed', 'Please select a valid breed from the options');
            } else {
                this.removeError(errors, 'breed');

                // If the pet breed query is not equal to the breed, assign the breed as the breed query
                if (strBreedQuery !== petBreed) {
                    petBreedQuery = petBreed;

                    // Retrieve the pet breed options using the new pet breed query
                    this.getPetBreedOptions(petBreedQuery);
                }
            }

            return { ...prevState, errors, petBreedQuery, petBreedOptions };
        });
    }

    handlePetCustomUnitChange(values) {
        let calculatedWeight = this.state.formData.weight;
        const customUnitValue = values.formattedValue;

        if (this.hasCustomUnit()) {
            calculatedWeight = parseFloat(customUnitValue) * this.state.customUnitRatio;
        }

        this.setState(prevState => {
            const formData = Pet({ ...prevState.formData, weight: calculatedWeight, customUnit: customUnitValue });
            return { formData };
        });
    }

    handlePetWeightChange(formattedValue) {
        // Create an event object for the pet weight change
        const objEvent = {
            target: {
                name: 'weight',
                value: formattedValue,
            },
        };

        this.handleInputChange(objEvent);
    }

    /**
     * Function call from the parent component
     */
    async handleSubmit() {
        let errors = [];
        try {
            await this.setState((prevState) => {
                // Check if there is no selected breed
                const hasNotSelectedPetBreed = this.getError('breed') === 'Please select a valid breed from the options';

                errors = prevState.formData.validateRequiredData({
                    hasCustomUnit: this.hasCustomUnit(),
                });

                // if pet weight is 0
                if (this.state.formData.weight === 0) {
                    this.addError(errors, 'weight', 'The Pet Weight must be greater than 0');
                    this.addError(errors, 'customUnit', 'Must be greater than 0');
                }

                // If there is no selected breed, replace the error with no selected breed error
                if (hasNotSelectedPetBreed) {
                    this.addError(errors, 'breed', 'Please select a valid breed from the options');
                }

                return { errors };
            });
        } catch (error) {
            console.error(error);
        }
        // Returns the data if the form has no errors
        return errors.length > 0 ? null : this.state.formData;
    }

    /**
     * Gets the pet type options based on a search text
     */
    getPetTypeOptions() {
        getPetTypeList()
            .then((response) => {
                // Response array with objects = [{pet_type: 'value'}, {pet_type: 'value'}]
                const petTypeOptions = response.data.data.map((element) => {
                    return { name: element.pet_type, value: element.pet_type };
                });
                this.setState({ petTypeOptions });
            })
            .catch((error) => {
                console.error(error);
            });
    }

    /**
     * Gets the pet type options based on a search text
     * 
     * @param {String} searchQuery search term
     * @param {Array} prevOptions List of previous options
     * @param {Object} options List of other options
     */
    getPetBreedOptions = async (searchQuery = '', prevOptions, options) => {
        this.setState({ isLoading: true });

        if (!this.state.formData.type) {
            this.setState(prevState => {
                let errors = prevState.errors;

                // Remove breed field errors when breed query is empty
                if (isEmpty(searchQuery)) {
                    errors = this.removeError(prevState.errors, 'breed');
                } 
                
                if (!this.state.formData.type) {
                    this.addError(errors, 'breed', 'Pet type should be selected first');
                }

                return { petBreedOptions: [], petBreedQuery: '', isLoading: false, errors }
            });

            return {
                options: [],
                hasMore: false,
            };
        } else {
            try {
                const response = await getPetBreedList(this.state.formData.type, options.page, searchQuery);
                const petBreedOptions = response.data.data.map((breedData) => {
                    return { label: breedData.name, value: breedData.name, details: {
                        custom_unit: breedData.custom_unit,
                        custom_unit_to_kg_ratio: breedData.custom_unit_to_kg_ratio,
                    }};
                });
                // Sets the list options
                this.setState(prevState => {
                    let errors = prevState.errors;
    
                    // If there are no pet breed options for the query, show invalid breed error
                    if (isEmpty(petBreedOptions)) {
                        this.addError(errors, 'breed', 'Please enter valid breed');
                    }
    
                    return { petBreedOptions, errors, isLoading: false };
                });
    
                return {
                    options: petBreedOptions,
                    hasMore: response.data.current_page < response.data.last_page,
                    additional: {
                        page: options.page + 1,
                    },
                };
            } catch (error) {
                console.error(error);
            }
        }        
    }

    /**
     * Gets the selected breed index from the pet breed options
     *
     * @param {String} value
     *
     * @returns {Number}
     */
    getSelectedBreedIndex(value) {
        return this.state.petBreedOptions.findIndex(option => option.value === value)
    }

    removeError = (errorList, errorName) => {
        const errIndex = errorList.findIndex(error => error.field === errorName);
        if (errIndex >= 0) {
            const errors = errorList;
            errors.splice(errIndex, 1);
            return errors;
        }
        return errorList;
    }

    /**
     * Applies rotation logic to the current slide in the pet tag photo carousel
     */
    rotateImage() {
        // Get the current slide index
        const currentSlideIndex = this.petTagPhotoCarouselRef.current.splide.index;

        // Set the current rotation mode for the current slide
        this.currentRotationMode[currentSlideIndex] = (this.currentRotationMode[currentSlideIndex] + 1) === 4 ? 0 : ((this.currentRotationMode[currentSlideIndex] || 0) + 1);

        // Generate a rotation class based on the current rotation mode
        const strClassName = `rotated-image-${this.rotatedDegrees[this.currentRotationMode[currentSlideIndex]]}`;

        // Clear all rotation classes from the current slide
        this.petTagPhotoCarouselRef.current.slides[currentSlideIndex].querySelector('img').classList.remove('rotated-image-0', 'rotated-image-90', 'rotated-image-180', 'rotated-image-270');
        
        // Add the generated rotation class to the current slide
        this.petTagPhotoCarouselRef.current.slides[currentSlideIndex].querySelector('img').classList.add(strClassName);
    }

    hasError(fieldError) {
        return this.state.errors.some(error => error.field === fieldError);
    }

    /**
     * Get the provided field's error message
     *
     * @param {string} fieldError
     * @returns {string}
     */
     getError(fieldError) {
        const error = this.state.errors.filter(error => error.field === fieldError);

        if (error.length !== 0) {
            return error[0].message;
        }
    }

    /**
     * Adds an error to the error array
     *
     * @param {array} errors
     * @param {string} field
     * @param {string} message
     * @returns {void}
     */
    addError(errors, field, message) {
        const errIndex = errors.findIndex(error => error.field === field);

        if (errIndex < 0) {
            errors.push({
                field: field,
                message: message
            });
        } else {
            errors[errIndex].message = message;
        }
    }

    hasCustomUnit() {
        return this.state.weightUnit !== WEIGHT_UNIT.kg;
    }

    /**
     * Event handler for owner last name changes to mirror the changes to the pet last name.
     * This should only be called from the parent component (OwnerPetDetailsForm).
     *
     * @param {String} strLastName
     *
     * @returns {void}
     */
    onLastNameChange(strLastName) {
        this.setState(prevState => {
            return { formData: Pet({ ...prevState.formData, lastName: capitalizeWords(strLastName) }) };
        });
    }

    /**
     * Event handler for when the user selects an option in the dropdown using the *Tab* key
     *
     * @param {Event} objEvent Event object
     * @param {Function} fnHandler Function for the change handler of the field
     * @param {String} strErrorMessage Error message when selecting an option
     * @param {Object} objRef Object reference for the input field dropdown
     *
     * @returns {void}
     */
    onOptionsKeyDown(objEvent, fnHandler, strErrorMessage, objRef) {
        if (objEvent.key === 'Tab') {
            try {
                setTimeout(() => fnHandler(), 50)
            } catch(objError) {
                console.log(strErrorMessage, objError);
            }
        }
    }

    /**
     * Event handler for when the pet breed changes
     *
     * @param {String} strPetBreed Selected pet breed option value from the dropdown
     *
     * @returns {void}
     */
    onPetBreedChange(strPetBreed) {
        let weightUnit = '';
        let customUnitRatio = 1;
        const selectedBreedOption = this.getSelectedBreedIndex(strPetBreed);

        if (selectedBreedOption >= 0) {
            weightUnit = this.state.petBreedOptions[selectedBreedOption].details.custom_unit;
            customUnitRatio = this.state.petBreedOptions[selectedBreedOption].details.custom_unit_to_kg_ratio;
        }

        this.setState((prevState) => {
            let errors = prevState.errors;

            errors = this.removeError(errors, 'breed');

            const formData = Pet({ ...prevState.formData, ...{ breed: strPetBreed, customUnit: '' } });
            return { errors, formData, petBreedQuery: strPetBreed, weightUnit: !isEmpty(weightUnit) ? weightUnit : WEIGHT_UNIT.kg, customUnitRatio };
        });
    }

    /**
     * Event handler for when the pet type changes
     *
     * @param {String} strPetType Selected pet type option value from the dropdown
     *
     * @returns {void}
     */
    onPetTypeChange(strPetType) {
        // Stop if the pet type is not changed
        if (this.state.formData.type === strPetType) {
            return;
        }

        // Updates the state
        this.setState((prevState) => {
            const formData = { ...prevState.formData, ...{ type: strPetType, breed: '' } };
            let errors = this.removeError(prevState.errors, 'type');
            
            errors = this.removeError(errors, 'breed');
            
            return {
                formData,
                petBreedQuery: '',
                petBreedOptions: [],
                errors
            };
        });
    }

    /**
     * Event handler for when the pet sex changes
     *
     * @param {String} strPetType Selected pet sex option value from the dropdown
     *
     * @returns {void}
     */
    onPetSexChange(strSex) {
        this.setState((prevState) => {
            const formData = { ...prevState.formData, ...{ sex: strSex } };
            const errors = this.removeError(prevState.errors, 'sex');
            return { formData, errors };
        });
    }

    render() {
        return (
            <Form className={`pet-custom-inline-form ${this.props.className ? this.props.className : ''}`}>
                <div className="form-header">Pet Details</div>
                <Row className="mx-0">
                    <Col className="pl-0" lg={7}>
                        <Form.Row>
                            <Form.Group as={Col} controlId="firstName" className="control-group">
                                <Form.Label className={this.hasError('firstName') ? 'is-invalid' : ''}>PET FIRST NAME *</Form.Label>
                                <Form.Control
                                    className={
                                        this.hasError('firstName')
                                            ? 'form-control is-invalid'
                                            : 'form-control'
                                    }
                                    name="firstName"
                                    onChange={(event) => this.handleInputChange(event)}
                                    type="text"
                                    value={this.state.formData.firstName}
                                    disabled={this.props.petDetailsDisabled}
                                />
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group as={Col} controlId="lastName" className="control-group">
                                <Form.Label className={this.hasError('lastName') ? 'is-invalid' : ''}>PET LAST NAME *</Form.Label>
                                <Form.Control
                                    className={
                                        this.hasError('lastName')
                                            ? 'form-control is-invalid'
                                            : 'form-control'
                                    }
                                    name="lastName"
                                    onChange={(event) => this.handleInputChange(event)}
                                    type="text"
                                    value={this.state.formData.lastName}
                                    disabled={this.props.petDetailsDisabled}
                                />
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group as={Col} controlId="pet-type" className="control-group">
                                <Form.Label className={this.hasError('type') ? 'is-invalid' : ''}>PET TYPE *</Form.Label>
                                <CustomSelect 
                                    ref={this.dropdownRefs.type}
                                    search={true}
                                    options={this.state.petTypeOptions}
                                    name="type"
                                    controlClassName={this.hasError('type') ? 'is-invalid' : ''}
                                    hasError={this.hasError('type')}
                                    value={this.state.formData.type}
                                    disabled={this.props.petDetailsDisabled}
                                    onChange={(value) => {
                                        this.onPetTypeChange(value);
                                    }}
                                    onKeyDown={e => this.onOptionsKeyDown(e, () => e.target.closest('.form-row').nextElementSibling.querySelector('.custom-select__input').focus(),  'Error in selecting pet type option', this.dropdownRefs.type)}
                                />
                                {/* <SelectSearch
                                    ref={this.dropdownRefs.type}
                                    options={this.state.petTypeOptions}
                                    name="type"
                                    autoComplete="off"
                                    filterOptions={(options) => {
                                        const fuse = new Fuse(options, {
                                            keys: ["name"],
                                            threshold: 0.1
                                        });


                                        return (value) => {
                                            if (!value.length) {
                                                return options;
                                            }

                                            let searchResult = fuse.search(value);
                                            let finalSearchResult = (searchResult) ? searchResult.map(data => data.item) : options;

                                            return finalSearchResult.sort((a, b) => (a.name > b.name) ? 1 : -1);;
                                        };
                                    }}
                                    search={true}
                                    controlClassName={this.hasError('type') ? 'is-invalid' : ''}
                                    hasError={this.hasError('type')}
                                    value={this.state.formData.type}
                                    disabled={this.props.petDetailsDisabled}
                                    onChange={(value) => {
                                        this.onPetTypeChange(value);

                                        // If the enter key is pressed, set it back to false and then focus back to the dropdown
                                        if (this.bEnterPressed) {
                                            this.bEnterPressed = false;
                                            setTimeout(() => this.dropdownRefs.type.current.querySelector('input').focus(), 50);
                                        }
                                    }}
                                    renderValue={(valueProps) => (
                                        <Form.Control
                                            {...valueProps}
                                            className={this.hasError('type') ? 'is-invalid' : ''}
                                            name="type"
                                            type="text"
                                            onMouseDownCapture={objEvent => {
                                                this.bOpenOptions = true;
                                            }}
                                            onFocusCapture={objEvent => {
                                                if (!this.bOpenOptions) {
                                                    objEvent.stopPropagation();
                                                } else {
                                                    this.bOpenOptions = false;
                                                }
                                            }}
                                            onKeyDownCapture={e => this.onOptionsKeyDown(e, e => this.onPetTypeChange(e), 'Error in selecting pet type option', this.dropdownRefs.type)}
                                        />
                                    )}
                                /> */}
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group as={Col} controlId="breed" className="control-group">
                                <Form.Label className={this.hasError('breed') ? 'is-invalid' : ''}>BREED *</Form.Label>
                                <CustomSelect 
                                    isLoading={this.state.isLoading}
                                    ref={this.dropdownRefs.breed}
                                    key={`breed-for-${this.state.formData.type}`}
                                    autoload={this.state.autoloadBreedDropdown}
                                    async={true}
                                    loadOptions={this.getPetBreedOptions}
                                    placeholder="Select pet type before searching..."
                                    search={true}
                                    options={this.state.petBreedOptions}
                                    name="type"
                                    controlClassName={this.hasError('breed') ? 'is-invalid' : ''}
                                    hasError={this.hasError('breed')}
                                    value={this.state.formData.breed}
                                    disabled={this.props.petDetailsDisabled}
                                    onKeyDown={e => this.onOptionsKeyDown(e, () => e.target.closest('.form-row').nextElementSibling.querySelector('.custom-select__input').focus(),  'Error in selecting pet breed option', this.dropdownRefs.breed)}
                                    onChange={(value) => {
                                        this.onPetBreedChange(value);
                                    }}
                                    onBlur={event => {
                                        // Only trigger the pet breed blur event handler if there are pet breed options or there is a previously selected breed
                                        if (this.state.petBreedOptions.length !== 0 || !isEmpty(this.state.formData.breed)) {
                                            this.handlePetBreedBlur(getFormFieldVal(event).breed);
                                        }
                                    }}
                                />
                                {/* <SelectSearch
                                    options={this.state.petBreedOptions}
                                    search={ true }
                                    autoComplete="off"
                                    filterOptions={(options) => {
                                        const fuse = new Fuse(options, {
                                            keys: ["name"],
                                            threshold: 0.1
                                        });

                                        return (value) => {
                                            if (!value.length) {
                                                return options;
                                            }

                                            let searchResult = fuse.search(value);
                                            let finalSearchResult = (searchResult) ? searchResult.map(data => data.item) : options;

                                            return finalSearchResult.sort((a, b) => (a.name > b.name) ? 1 : -1);;
                                        };
                                    }}
                                    value={this.state.formData.breed}
                                    disabled={this.props.petDetailsDisabled}
                                    closeOnSelect={ true }
                                    ref={this.dropdownRefs.breed}
                                    onChange={(value) => {
                                        this.onPetBreedChange(value);

                                        // If the enter key is pressed, set it back to false and then focus back to the dropdown
                                        if (this.bEnterPressed) {
                                            this.bEnterPressed = false;
                                            setTimeout(() => this.dropdownRefs.breed.current.querySelector('input').focus(), 50);
                                        }
                                    }}
                                    onBlur={event => {
                                        // Only trigger the pet breed blur event handler if there are pet breed options or there is a previously selected breed
                                        if (this.bOpenOptions && this.state.petBreedOptions.length !== 0 || !isEmpty(this.state.formData.breed)) {
                                            this.handlePetBreedBlur(getFormFieldVal(event).breed);
                                        }
                                    }}
                                    // className={this.state.petBreedOptions.length > 0 ? 'pet-select-search' : 'hide-options'}
                                    renderValue={(valueProps) => (
                                        <Form.Control
                                            {...valueProps}
                                            className={this.hasError('breed') ? 'is-invalid' : ''}
                                            name="breed"
                                            onChange={(event) => { this.handleBreedSearchInputChange(event); }}
                                            type="text"
                                            placeholder="Select pet type before searching..."
                                            value={this.state.petBreedQuery}
                                            onMouseDownCapture={objEvent => {
                                                this.bOpenOptions = true;
                                            }}
                                            onFocusCapture={objEvent => {
                                                if (!this.bOpenOptions) {
                                                    objEvent.stopPropagation();
                                                } else {
                                                    this.bOpenOptions = false;
                                                }
                                            }}
                                            onKeyDownCapture={e => this.onOptionsKeyDown(e, e => this.onPetBreedChange(e), 'Error in selecting pet breed option', this.dropdownRefs.breed)}
                                        />
                                    )}
                                /> */}
                                <Form.Control.Feedback type="invalid">{this.getError('breed')}</Form.Control.Feedback>
                            </Form.Group>
                        </Form.Row>
                        <Form.Row>
                            <Form.Group as={Col} controlId="sex" className="control-group">
                                <Form.Label className={this.hasError('sex') ? 'is-invalid' : ''}>SEX *</Form.Label>
                                <CustomSelect 
                                    ref={this.dropdownRefs.sex}
                                    search={true}
                                    options={SEX_OPTIONS}
                                    name="sex"
                                    controlClassName={this.hasError('sex') ? 'is-invalid' : ''}
                                    hasError={this.hasError('sex')}
                                    value={this.state.formData.sex}
                                    disabled={this.props.petDetailsDisabled}
                                    onChange={(value) => {
                                        this.onPetSexChange(value);
                                    }}
                                    onKeyDown={e => this.onOptionsKeyDown(e, () => e.target.closest('.form-row').nextElementSibling.querySelector('input.form-control').focus(),  'Error in selecting pet sex option', this.dropdownRefs.sex)}
                                />
                                {/* <SelectSearch
                                    ref={this.dropdownRefs.sex}
                                    options={SEX_OPTIONS}
                                    autoComplete="off"
                                    name="sex"
                                    filterOptions={(options) => {
                                        const fuse = new Fuse(options, {
                                            keys: ["name"],
                                            threshold: 0.1
                                        });


                                        return (value) => {
                                            if (!value.length) {
                                                return options;
                                            }

                                            let searchResult = fuse.search(value);
                                            return (searchResult) ? searchResult.map(data => data.item) : options;
                                        };
                                    }}
                                    search={true}
                                    controlClassName={this.hasError('sex') ? 'is-invalid' : ''}
                                    hasError={this.hasError('sex')}
                                    value={this.state.formData.sex}
                                    disabled={this.props.petDetailsDisabled}
                                    onChange={(value) => {
                                        this.onPetSexChange(value);

                                        // If the enter key is pressed, set it back to false and then focus back to the dropdown
                                        if (this.bEnterPressed) {
                                            this.bEnterPressed = false;
                                            setTimeout(() => this.dropdownRefs.sex.current.querySelector('input').focus(), 50);
                                        }
                                    }}
                                    renderValue={(valueProps) => (
                                        <Form.Control
                                            {...valueProps}
                                            className={this.hasError('sex') ? 'is-invalid' : ''}
                                            name="sex"
                                            type="text"
                                            onMouseDownCapture={objEvent => {
                                                this.bOpenOptions = true;
                                            }}
                                            onFocusCapture={objEvent => {
                                                if (!this.bOpenOptions) {
                                                    objEvent.stopPropagation();
                                                } else {
                                                    this.bOpenOptions = false;
                                                }
                                            }}
                                            onKeyDownCapture={e => this.onOptionsKeyDown(e, e => this.onPetSexChange(e), 'Error in selecting pet sex option', this.dropdownRefs.sex)}
                                        />
                                    )}
                                /> */}
                            </Form.Group>
                        </Form.Row>
                        {
                            !this.hasCustomUnit() ?
                            <Form.Row>
                                <Form.Group as={Col} controlId="weight" className="control-group">
                                    <Form.Label className={this.hasError('weight') ? 'is-invalid' : ''}>{WEIGHT_UNIT.kg} *</Form.Label>
                                    <NumberFormat
                                        className={
                                            this.hasError('weight')
                                                ? 'form-control is-invalid'
                                                : 'form-control'
                                        }
                                        value={this.state.formData.weight}
                                        onValueChange={values => this.handlePetWeightChange(values.formattedValue)}
                                        name="weight"
                                        decimalScale={2}
                                        allowNegative={false}
                                        disabled={this.props.petDetailsDisabled ? true: (this.state.formData.weight > 0 && localStorage.getItem('is-pet-next')) || this.props.isEdit ? (this.props.petWeightEditable && !localStorage.getItem('is-pet-next') ? false : true) : false}
                                        fixedDecimalScale={true}
                                    />
                                    <Form.Control.Feedback type="invalid">{this.getError('weight')}</Form.Control.Feedback>
                                    {
                                        this.props.petDetailsDisabled ? <p className="margin-top">Should you need to change pet weight, please contact our Client Care team</p> : (this.state.formData.weight > 0 && localStorage.getItem('is-pet-next')) || this.props.isEdit ? 
                                        this.props.petWeightEditable && !localStorage.getItem('is-pet-next') ? '' : <p className="margin-top">Should you need to change pet weight, please contact our Client Care team</p> : ''
                                    }
                                </Form.Group>
                            </Form.Row> :
                            <Form.Row>
                                <Form.Group as={Col} controlId="customUnit" className="control-group">
                                    <Form.Label className={this.hasError('customUnit') ? 'is-invalid' : ''}>{this.state.weightUnit} *</Form.Label>
                                    <NumberFormat
                                        className={
                                            this.hasError('customUnit')
                                                ? 'form-control is-invalid'
                                                : 'form-control'
                                        }
                                        value={this.state.formData.customUnit}
                                        onValueChange={values => this.handlePetCustomUnitChange(values)}
                                        name="customUnit"
                                        decimalScale={2}
                                        allowNegative={false}
                                        fixedDecimalScale={true}
                                    />
                                    <Form.Control.Feedback type="invalid">{this.getError('customUnit')}</Form.Control.Feedback>
                                    {
                                        this.props.petDetailsDisabled ? <p className="margin-top">Should you need to change, please contact our Client Care team</p> : (this.state.formData.weight > 0 && localStorage.getItem('is-pet-next')) || this.props.isEdit ? 
                                        this.props.petWeightEditable && !localStorage.getItem('is-pet-next') ? '' : <p className="margin-top">Should you need to change, please contact our Client Care team</p> : ''
                                    }
                                </Form.Group>
                            </Form.Row>
                        }
                    </Col>
                    <Col className="pl-0" lg={5}>
                        <Form.Label>Pet Tag Photo </Form.Label>  
                        <MdOutlineRefresh className="rotate-image" onClick={(event) => this.rotateImage(event)}/>
                        <Splide 
                            aria-label="Pet Tag Photo" 
                            options={{ omitEnd: true }}
                            ref={this.petTagPhotoCarouselRef}
                        >
                        {
                            this.props.bookingImage && !isEmpty(this.props.bookingImage) ? 
                            this.props.bookingImage.map((bookingImage, index) => {
                                return (
                                    <SplideSlide key={`pet-tag-photo-${index}`}>
                                        <Image
                                            className="pet-tag-photo"
                                            src={bookingImage ? bookingImage : imageFallback}
                                            onError={(evt) => {
                                                evt.target.src = imageFallback;
                                            }}
                                        />
                                    </SplideSlide>
                                );
                            }) : 
                            <SplideSlide>
                                <Image
                                    className="pet-tag-photo"
                                    src={imageFallback}
                                    onError={(evt) => {
                                        evt.target.src = imageFallback;
                                    }}
                                />
                            </SplideSlide>
                        }
                        </Splide>
                    </Col>
                </Row>
            </Form>
        );
    }
}

export default PetDetailsForm;
